import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { BwjPageHeader } from '../../../../framework/components/bwj-page-header';
import { CustomerAccountService, useProjectConfiguration } from '../../../../services/customer-account';
import { AppRoutes, useProjectInstallationSite } from '../../../app-routes';
import { ProjectService, useDoorSortingPreference } from '../../../services/project';
import { DoorType } from '../../../../entities-generated/common/door-type';
import { useNavigator } from '../../../../framework/services/navigation';
import { useStandardErrorState } from '../../../../entities/view-manager/error';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { fireToggleShoppingPrefsPanel } from '../../../events';
import { DoorInstallationModel, IDoorInstallationModel } from '../../../../entities-generated/models/project/door-installation-model';
import { ShopStormDoorApiClient } from '../../../../services/shop-storm-door';
import { IStormDoorModel } from '../../../../entities-generated/models/storm-door/storm-door-model';
import { DoorSortMode } from '../../entities/door-sort-options';
import { firstOrDefault, orderBy, orderByDesc, where } from '../../../../framework/utils/collection';
import { StormDoorTile } from '../../components/st-door-tile';
import { ProductTileContainer } from '../../components/product-tile-container';
import { toTitleText } from '../../../../framework/utils/string';
import { StormColorPreferencePanel } from '../../components/st-color-pref-panel';
import { StormDoorColor } from '../../../../entities-generated/common/storm-door-color';
import { ColorApiClient } from '../../../../services/color-prefs';
import { IUpdateStormDoorColorPreferenceRequest } from '../../../../entities-generated/models/storm-door/update-storm-door-color-preference-request';
import { getTheme, MessageBarType } from '@fluentui/react';
import { IProductTexture } from '../../../../entities-generated/common/product-texture';
import { BwjEventArgs } from '../../../../framework/entities/bwj-event';
import { IStormDoorDetailPanelArgs, StormDoorDetailPanel } from '../../components/st-door-detail-panel';
import { IncludedServicesPanel } from '../../components/incl-services-panel';
import { IShowAnnouncementBannerArgs } from '../../../../framework/components/bwj-announcement/props';
import { IProjectSelectionRequest } from '../../../../entities-generated/models/project/project-selection-request';
import { BwjAnnouncementBanner } from '../../../../framework/components/bwj-announcement';
import { ShoppingIntroDialog } from '../../components/shop-intro-dialog';
import { TutorialService } from '../../../services/tutorial';
import { ToastService } from '../../../../framework/services/toast';
import { DoorRecommendation } from '../../../../entities-generated/common/door-recommendation';
import * as SelectionPageServices from '../../services/selection-page';
import { isThisLocalhost } from '../../../../framework/utils';

const debuggin = isThisLocalhost();

const subscriberId = 'Page.ShopStormDoors';

export const PageShopStormDoors: React.FunctionComponent = () => {
  const theme = getTheme();
  const cs = SelectionPageServices.getStyles(theme);
  const nav = useNavigator();
  const location = useProjectInstallationSite();
  const projectConfig = useProjectConfiguration(subscriberId);
  const errorState = useStandardErrorState();
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const loadingMessage = useRef('Loading best door options...');
  const installation = useRef<IDoorInstallationModel>(new DoorInstallationModel());
  const initialLoad = useRef(true);
  const previousSelectionMade = useRef(false);
  const introMessage = useRef((<></>));
  const [showIntroDialog, setShowIntroDialog] = useState(false);
  const [rawDoorOptions, setRawDoorOptions] = useState<IStormDoorModel[]>([]);
  const sortMode = useDoorSortingPreference(subscriberId);
  const [launchDoorDetailsPanel, setLaunchDoorDetailsPanel] = useState<BwjEventArgs<IStormDoorDetailPanelArgs> | undefined>(undefined);
  const [includedServicesDisplay, setIncludedServicesDisplay] = useState<string[] | null>(null);
  const [invokeShowSaveError, setInvokeShowSaveError] = useState<BwjEventArgs<IShowAnnouncementBannerArgs | undefined> | undefined>(undefined);
  const doorOptions = useMemo(() => {
    switch(sortMode) {
      case DoorSortMode.Bestseller:
        return orderByDesc(rawDoorOptions, d => d.productReview.totalReviewCount);
      case DoorSortMode.Price:
        return orderBy(rawDoorOptions, d => d.price);
      case DoorSortMode.Quality:
        return orderByDesc(rawDoorOptions, d => d.price);
      case DoorSortMode.Rating:
      default:
        return orderByDesc(rawDoorOptions, d => d.rating);
    };
  }, [rawDoorOptions, sortMode]);
  const recommendedOptions = useMemo(() => {
    const options = where(rawDoorOptions, d => d.recommendation !== DoorRecommendation.Standard);
    return orderByDesc(options, d => d.recommendation);
  }, [rawDoorOptions]);

  const showSaveError = () => {
    setInvokeShowSaveError(new BwjEventArgs(SelectionPageServices.saveErrorArgs));
    window.setTimeout(() => setInvokeShowSaveError(new BwjEventArgs(undefined)), 5000);
  }

  const showLoading = () => {
    fireToggleShoppingPrefsPanel(false);
    setViewState(BwjViewState.Loading);
  }

  const showReady = () => {
    fireToggleShoppingPrefsPanel(true);
    setViewState(BwjViewState.Ready);
  }

  const themeColor = theme.palette.themePrimary;
  const refreshDoorOptions = useCallback(() => {
    if(loadingMessage.current !== 'Loading best door options...') { // loading for the first time
      loadingMessage.current = 'Updating your options...';
    }
    showLoading();

    ShopStormDoorApiClient.getStormDoorOptions(installation.current.id)
    .then(resp => {
      setRawDoorOptions(resp);
      const vs = resp.length > 0 ? BwjViewState.Ready : BwjViewState.NoSearchResults;
      setViewState(vs);
      fireToggleShoppingPrefsPanel(true);
      loadingMessage.current = '';

      if(initialLoad.current && previousSelectionMade.current === false) {
        initialLoad.current = false;
        introMessage.current = installation.current.configurationIndex === 0
        ? (<span>
            Welcome {projectConfig.myName}! Let&#39;s start by picking out a storm door for your <strong style={{ color: themeColor}}>{location.replaceAll('-', ' ')}</strong>
          </span>)
        : (<span>
            Now let&#39;s choose a storm door for your <strong style={{ color: themeColor}}>{location.replaceAll('-', ' ')}</strong>
          </span>);
        setShowIntroDialog(true);
      }
    })
    .catch(() => {
      setViewState(BwjViewState.Error);
    });
  }, [location, projectConfig, themeColor]);

  useEffect(() => {
    const doorInstall = ProjectService.getDoorInstallation(location, DoorType.StormDoor, projectConfig);
    if(!doorInstall // no such install
      || doorInstall.isExcluded
      // normally, all preceding doors must be configured, but there could be an exception in the case an excluded door prior to the current door
      // is included again.  In this case, its okay to configure the current door.  This can only happen if the current door is selected from the cart.
      // Before sending the user here, the cart will register a one-time exception to the 'preceding doors' rule
      || (ProjectService.precedingDoorsConfigured(doorInstall, projectConfig) === false && ProjectService.allowConfigOrderException() === false)) {
        // We go home instead of just going directly to the next logical page.  This is done intentionally so that, in the case of consecutive
        // door choices, this instance of the page is destroyed and not reused
      nav.goTo(AppRoutes.home.url());
      return;
    }

    ProjectService.clearConfigOrderException();
    installation.current = doorInstall;
    previousSelectionMade.current = !!doorInstall.selectedDoor;
    refreshDoorOptions();

  }, [location, projectConfig, nav, refreshDoorOptions]);

  const onUpdateColorPreferences = (prefs: StormDoorColor[]) => {
    fireToggleShoppingPrefsPanel(false);
    loadingMessage.current = 'Saving your preferences...';
    setViewState(BwjViewState.Loading);

    const request: IUpdateStormDoorColorPreferenceRequest = {
      colorPreferences: prefs,
      doorInstallationId: installation.current.id,
    };
    ColorApiClient.updateStormDoorColorPreferences(request)
    .then(resp => {
      CustomerAccountService.updateProjectConfiguration(resp);
      // we don't call showReady because it brings the panel back, and we'll have to reload the options right now
      setViewState(BwjViewState.Ready);  
    })
    .catch(() => {
      ToastService.show(MessageBarType.error, 'Sorry, something went wrong. Please try again');
      showReady();
    });
  }

  const onViewDoorDetails = (door: IStormDoorModel) => {
    debuggin && console.log(door);
    
    const itl = installation.current;
    const services = itl.includedInPrice;
    const variantId = itl.selectedDoor?.id === door.id ? itl.selectedDoor.textureVariant.id : null;
    const selected = firstOrDefault(door.textureVariants, v => v.id === variantId);
    const priceChange = itl.priceChange;
    const args = { door, services, selected, priceChange };
    setLaunchDoorDetailsPanel(new BwjEventArgs(args));
  }

  const onDoorSelected = (door: IStormDoorModel, variant: IProductTexture) => {
    loadingMessage.current = 'Saving your selection...';
    showLoading();

    const request: IProjectSelectionRequest = {
      doorInstallationId: installation.current.id,
      selectionId: {
        productId: door.id,
        variantId: variant.id
      }
    };
    ShopStormDoorApiClient.selectStormDoor(request)
    .then(resp => {
      CustomerAccountService.updateProjectConfiguration(resp);
      ToastService.show(MessageBarType.success, `Selected ${door.description}`);
      nav.goTo(AppRoutes.home.url());
    })
    .catch(() => {
      showReady();
      showSaveError();
    });
  };

  const onIncludedServicesOverlayRequested = (included: string[]) => {
    setIncludedServicesDisplay(included);
  };
  const onCloseIncludedServicesOverlay = () => {
    setIncludedServicesDisplay(null);
  };

  const onCloseShoppingIntroDialog = () => {
    setShowIntroDialog(false);
    if(installation.current.configurationIndex === 0) {
      TutorialService.teachShoppingPreferencesMenu();
    } else if(previousSelectionMade.current === false) {
      TutorialService.teachShoppingCart();
    }
  }

  return (
    <>
    <ShoppingIntroDialog
      showDialog={showIntroDialog}
      title={'Choose A Storm Door'}
      richContent={introMessage.current}
      onClose={onCloseShoppingIntroDialog}
    />
    <StormDoorDetailPanel
      launchPanel={launchDoorDetailsPanel}
      onDoorSelected={onDoorSelected}
      onIncludedServicesOverlayRequested={onIncludedServicesOverlayRequested}
      includedServicesOnDisplay={!!includedServicesDisplay}
    />
    <StormColorPreferencePanel
      onUpdatePreferences={onUpdateColorPreferences}
    />
    <IncludedServicesPanel
      includedServices={includedServicesDisplay}
      onClose={onCloseIncludedServicesOverlay}
    />
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: loadingMessage.current,
      }}
      errorState={errorState}
      noSearchResultsState={{
        icon: 'SearchAndApps',
        message: 'No doors found, but don\'t give up - try expanding your color preferences',
      }}
    >
      <BwjAnnouncementBanner
        invokeShowAnnouncement={invokeShowSaveError}
        closeDialogButtonText={'Close'}
        ariaCloseBannerMessage={'Dismiss message'}
        ariaShowFullMessageDialog={'Show full message'}
      />
      <BwjPageHeader
        title={`Storm Door - ${toTitleText(location)}`}
      />
      <BwjPageContent contentDescription={'primary content (no subcontent)'}>
        {recommendedOptions.length > 0 &&
        <div className={cs.recommendedSection}>
          <h2 className={cs.recommendedHeading}>
            <span>Recommended</span>
            <span className='subtext'>based on your previous selections</span>
          </h2>
          <ProductTileContainer>
            {recommendedOptions.map(door =>
              <StormDoorTile
                key={door.id}
                idPrefix='recmd-tile'
                door={door}
                onSelected={onViewDoorDetails}
                recommendation={SelectionPageServices.recommendedSectionRecommendation(door.recommendation)}
              />
            )}
          </ProductTileContainer>
        </div>}

        {recommendedOptions.length > 0 &&
        <h2 className={cs.generalHeading}>
          All Storm Doors
        </h2>}
        <ProductTileContainer>
          {doorOptions.map(door =>
            <StormDoorTile
              key={door.id}
              idPrefix='all-tile'
              door={door}
              onSelected={onViewDoorDetails}
              recommendation={door.recommendation}
            />
          )}
        </ProductTileContainer>
      </BwjPageContent>
    </BwjViewManager>
    </>
  );
};